import React, { useState, useCallback, createContext, useContext } from "react"

export type GuestInputValues = Record<string, string>

interface BoothSessionState {
  rawImage: string | null
  setRawImage: (image: string | null) => void
  aiProcessedImage: string | null
  setAiProcessedImage: (image: string | null) => void
  selectedAiImageIndex: number
  aiImageHistory: string[]
  compositeImage: string | null
  setCompositeImage: (image: string | null) => void
  addAiImage: (image: string) => void
  selectAiImage: (index: number) => void
  guestInputValues: GuestInputValues
  setGuestInputValues: (values: GuestInputValues) => void
  clearAiImages: () => void
  clearImages: () => void
}

const BoothSessionContext = createContext<BoothSessionState | null>(null)

export function BoothSessionProvider({ children }: { children: React.ReactNode }) {
  const [rawImage, setRawImage] = useState<string | null>(null)
  const [aiProcessedImage, setAiProcessedImageState] = useState<string | null>(null)
  const [compositeImage, setCompositeImage] = useState<string | null>(null)
  const [aiImageHistory, setAiImageHistory] = useState<string[]>([])
  const [selectedAiImageIndex, setSelectedAiImageIndex] = useState(0)
  const [guestInputValues, setGuestInputValues] = useState<GuestInputValues>({})

  const setAiProcessedImage = useCallback((image: string | null) => {
    setAiProcessedImageState(image)
  }, [])

  const addAiImage = useCallback(
    (image: string) => {
      setAiImageHistory((prev) => {
        const updated = [...prev, image]
        const newIndex = updated.length - 1
        setSelectedAiImageIndex(newIndex)
        return updated
      })
      setAiProcessedImage(image)
    },
    [setAiProcessedImage],
  )

  const selectAiImage = useCallback(
    (index: number) => {
      setAiImageHistory((history) => {
        if (index >= 0 && index < history.length) {
          setSelectedAiImageIndex(index)
          setAiProcessedImage(history[index])
        }
        return history
      })
    },
    [setAiProcessedImage],
  )

  const clearAiImages = useCallback(() => {
    setAiProcessedImage(null)
    setAiImageHistory([])
    setSelectedAiImageIndex(0)
  }, [setAiProcessedImage])

  const clearImages = useCallback(() => {
    setRawImage(null)
    setAiProcessedImage(null)
    setCompositeImage(null)
    setAiImageHistory([])
    setSelectedAiImageIndex(0)
    setGuestInputValues({})
  }, [setAiProcessedImage])

  const value: BoothSessionState = {
    rawImage,
    setRawImage,
    aiProcessedImage,
    setAiProcessedImage,
    selectedAiImageIndex,
    aiImageHistory,
    compositeImage,
    setCompositeImage,
    addAiImage,
    selectAiImage,
    guestInputValues,
    setGuestInputValues,
    clearAiImages,
    clearImages,
  }

  return React.createElement(BoothSessionContext.Provider, { value }, children)
}

export function useBoothSession(): BoothSessionState {
  const context = useContext(BoothSessionContext)
  if (!context) {
    throw new Error("useBoothSession() called outside of a BoothSessionProvider")
  }
  return context
}
